import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore, THEMES } from '../../stores/themeStore';
import { colors } from '../../styles/colors';
import { Capacitor } from '@capacitor/core';

/** Google "G" logo SVG */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const login = useAuthStore((s) => s.login);
  const googleSignIn = useAuthStore((s) => s.googleSignIn);
  const appleSignIn = useAuthStore((s) => s.appleSignIn);
  const loading = useAuthStore((s) => s.loading);
  const sessionConflict = useAuthStore((s) => s.sessionConflict);
  const resolveSessionConflict = useAuthStore((s) => s.resolveSessionConflict);
  const cancelSessionConflict = useAuthStore((s) => s.cancelSessionConflict);
  const chatTheme = useThemeStore((s) => s.chatTheme);
  const isKo = i18n.language === 'ko';

  const [email, setEmail] = useState(() => localStorage.getItem('ourhertz_remembered_email') ?? '');
  const [password, setPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(() => !!localStorage.getItem('ourhertz_remembered_email'));
  const [autoLogin, setAutoLogin] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (rememberEmail) localStorage.setItem('ourhertz_remembered_email', email);
    else localStorage.removeItem('ourhertz_remembered_email');
    if (autoLogin) localStorage.setItem('ourhertz_auto_login', 'true');
    else localStorage.removeItem('ourhertz_auto_login');

    try {
      await login(email, password);
    } catch {
      setError(t('auth.loginFailed'));
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    try {
      await googleSignIn(false);
    } catch (err: any) {
      const msg = err.message ?? '';
      if (msg === 'NOT_REGISTERED') {
        setError(isKo
          ? '가입되지 않은 계정입니다. 회원가입 페이지에서 가입해주세요.'
          : 'Account not found. Please sign up first.');
      } else if (msg.startsWith('COOLDOWN:')) {
        const days = msg.split(':')[1];
        setError(t('auth.deletionCooldown', { days }));
      } else if (msg.startsWith('EMAIL_EXISTS_DIFFERENT_PROVIDER')) {
        const existingEmail = msg.split(':')[1] ?? '';
        const shown = existingEmail ? `(${existingEmail}) ` : '';
        setError(isKo
          ? `이 이메일 ${shown}은(는) 이미 다른 방식(이메일/비밀번호 또는 Apple)으로 가입되어 있습니다. 해당 방식으로 먼저 로그인한 뒤, 이메일 인증을 완료해 주세요. 그 후에는 Google 로그인도 같은 계정에 자동으로 연결됩니다.`
          : `This email ${shown}is already registered with a different sign-in method (email/password or Apple). Please sign in with that method first and verify your email — Google sign-in will then be linked automatically to the same account.`);
      } else if (err.code !== 'auth/popup-closed-by-user') {
        setError(isKo ? 'Google 로그인에 실패했습니다.' : 'Google login failed.');
      }
    }
  };

  const handleAppleLogin = async () => {
    setError('');
    try {
      await appleSignIn(false);
    } catch (err: any) {
      const msg = err.message ?? '';
      if (msg === 'NOT_REGISTERED') {
        setError(isKo
          ? '가입되지 않은 계정입니다. 회원가입 페이지에서 가입해주세요.'
          : 'Account not found. Please sign up first.');
      } else if (msg.startsWith('COOLDOWN:')) {
        const days = msg.split(':')[1];
        setError(t('auth.deletionCooldown', { days }));
      } else if (msg.startsWith('EMAIL_EXISTS_DIFFERENT_PROVIDER')) {
        const existingEmail = msg.split(':')[1] ?? '';
        const shown = existingEmail ? `(${existingEmail}) ` : '';
        setError(isKo
          ? `이 이메일 ${shown}은(는) 이미 다른 방식(이메일/비밀번호 또는 Google)으로 가입되어 있습니다. 해당 방식으로 먼저 로그인한 뒤, 이메일 인증을 완료해 주세요. 그 후에는 Apple 로그인도 같은 계정에 자동으로 연결됩니다.`
          : `This email ${shown}is already registered with a different sign-in method (email/password or Google). Please sign in with that method first and verify your email — Apple sign-in will then be linked automatically to the same account.`);
      } else if (msg === 'cancelled' || msg.includes('popup-closed') || msg.includes('canceled') || msg.includes('cancel')) {
        // User cancelled — silent
      } else {
        // Surface the underlying error to help App Store reviewers / TestFlight testers diagnose.
        const detail = msg.replace(/^Apple Sign-In failed:\s*/, '');
        setError(isKo ? `Apple 로그인 실패: ${detail}` : `Apple login failed: ${detail}`);
      }
    }
  };

  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  const shareLinks = [
    { key: 'web' as const, label: 'Web', icon: '🌐', color: colors.textSecondary },
    ...(!isIOS ? [{ key: 'android' as const, label: 'Android', icon: '🤖', color: '#3ddc84' }] : []),
    { key: 'ios' as const, label: 'iOS', icon: '🍎', color: '#007aff' },
  ];

  const handleShare = async (platform: 'web' | 'android' | 'ios') => {
    setShowShareMenu(false);
    const { shareAppForPlatform } = await import('../../utils/share');
    await shareAppForPlatform(platform, i18n.language);
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center px-6"
      style={{ backgroundColor: colors.background }}>

      {/* Top right: Language + Theme + Share */}
      <div className="absolute top-4 right-4 flex gap-1.5">
        <button onClick={() => { const l = i18n.language === 'ko' ? 'en' : 'ko'; i18n.changeLanguage(l); localStorage.setItem('ourhertz_language', JSON.stringify(l)); }}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-bold"
          style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          {isKo ? 'EN' : '한'}
        </button>
        <button onClick={() => {
          const ts = useThemeStore.getState();
          const idx = THEMES.findIndex(t => t.key === ts.chatTheme);
          ts.setChatTheme(THEMES[(idx + 1) % THEMES.length].key);
        }}
          className="flex items-center gap-0.5 rounded px-2 py-1 text-xs font-bold"
          style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
          🎨 {(() => { const th = THEMES.find(t => t.key === chatTheme); return isKo ? th?.label.ko : th?.label.en; })()}
        </button>
        <div className="relative">
          <button onClick={() => setShowShareMenu(!showShareMenu)}
            className="rounded px-2 py-1 text-xs" style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          </button>
          {showShareMenu && (
            <div className="absolute right-0 top-full z-10 mt-1 overflow-hidden rounded-lg shadow-xl"
              style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}`, minWidth: 140 }}>
              {shareLinks.map(({ key, label, icon, color }) => (
                <button key={key} onClick={() => handleShare(key)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold hover:bg-white/5"
                  style={{ color }}>
                  {icon} {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Logo + App description */}
      <h1 className="mb-1 text-4xl font-bold" style={{
        background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}>
        {isKo ? '우리들의 주파수' : 'OurHertz'}
      </h1>
      {isKo ? <p className="mb-2 text-sm font-semibold" style={{ color: colors.textSecondary }}>OurHertz</p> : <div className="mb-2" />}
      <p className="mb-1 max-w-[340px] text-center text-xs leading-relaxed" style={{ color: colors.textMuted, wordBreak: 'keep-all' }}>
        {isKo
          ? 'GPS 기반 무전기 채팅 서비스. 내 위치가 주파수가 되어 주변 사람들과 실시간으로 소통하세요.'
          : 'GPS-based walkie-talkie chat. Your location becomes your frequency — connect with nearby people in real time.'}
      </p>
      <div className="mb-5 flex items-center gap-3 text-[10px]" style={{ color: colors.textMuted }}>
        <span>📡 {isKo ? '실시간 채팅' : 'Live Chat'}</span>
        <span>📍 {isKo ? '위치 메시지 드롭' : 'Location Drops'}</span>
        <span>🗺️ {isKo ? '레이더 지도' : 'Radar Map'}</span>
      </div>

      {/* Login card */}
      <div className="w-full max-w-[360px] rounded-xl p-5"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Email */}
          <input type="email" placeholder={t('auth.email')} value={email}
            onChange={(e) => setEmail(e.target.value)} required
            className="w-full rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none"
            style={{ borderColor: colors.border, color: colors.text }} />

          {/* Password */}
          <div className="relative">
            <input type={showPassword ? 'text' : 'password'} placeholder={t('auth.password')}
              value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full rounded-lg border bg-transparent px-3 py-2.5 pr-10 text-sm outline-none"
              style={{ borderColor: colors.border, color: colors.text }} />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm"
              style={{ color: colors.textMuted }}>
              {showPassword ? '🙈' : '👁'}
            </button>
          </div>

          {/* Remember + Auto login */}
          <div className="flex items-center justify-between text-xs" style={{ color: colors.textSecondary }}>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={rememberEmail}
                onChange={(e) => setRememberEmail(e.target.checked)}
                className="h-3.5 w-3.5 rounded accent-[#4CAF50]" />
              {t('auth.rememberEmail')}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={autoLogin}
                onChange={(e) => setAutoLogin(e.target.checked)}
                className="h-3.5 w-3.5 rounded accent-[#4CAF50]" />
              {t('auth.autoLogin')}
            </label>
          </div>

          {error && <p className="text-center text-xs" style={{ color: colors.error }}>{error}</p>}

          {/* Login button */}
          <button type="submit" disabled={loading}
            className="w-full rounded-lg py-2.5 text-sm font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: colors.primary }}>
            {loading ? t('common.loading') : t('auth.login')}
          </button>
        </form>

        {/* Divider */}
        <div className="my-4 flex items-center gap-3">
          <div className="flex-1 border-t" style={{ borderColor: colors.border }} />
          <span className="text-xs" style={{ color: colors.textMuted }}>{t('auth2.or')}</span>
          <div className="flex-1 border-t" style={{ borderColor: colors.border }} />
        </div>

        {/* Google login */}
        <button onClick={handleGoogleLogin} disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
          style={{ backgroundColor: colors.card, color: colors.text, border: `1px solid ${colors.border}` }}>
          <GoogleIcon />
          {t('auth2.googleLogin')}
        </button>

        {/* Apple login */}
        <button onClick={handleAppleLogin} disabled={loading}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
          style={{ backgroundColor: '#000', color: '#fff', border: `1px solid ${colors.border}` }}>
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.784 9.484c-.023-2.392 1.952-3.541 2.04-3.597-1.11-1.624-2.838-1.846-3.454-1.872-1.47-.149-2.87.866-3.616.866-.745 0-1.897-.844-3.116-.822-1.604.024-3.082.933-3.908 2.37-1.666 2.89-.426 7.17 1.197 9.516.794 1.147 1.74 2.436 2.982 2.39 1.195-.048 1.647-.774 3.092-.774 1.444 0 1.848.774 3.11.75 1.287-.022 2.103-1.17 2.892-2.32.912-1.331 1.287-2.62 1.31-2.687-.028-.013-2.513-.965-2.538-3.83l.01.01zM11.39 2.632c.66-.8 1.105-1.91.984-3.017-.951.039-2.103.615-2.78 1.395-.607.7-1.14 1.82-.996 2.894 1.062.083 2.147-.523 2.792-1.272z" fill="currentColor"/>
          </svg>
          {t('auth2.appleLogin')}
        </button>

        {/* Links */}
        <div className="mt-4 flex items-center justify-between text-xs">
          <Link to="/signup" className="font-semibold" style={{ color: colors.primary }}>
            {t('auth.signup')}
          </Link>
          <Link to="/forgot-password" style={{ color: colors.textSecondary }}>
            {t('auth.forgotPassword')}
          </Link>
        </div>
      </div>

      {/* Footer: legal links + biz info + version */}
      <div className="mt-6 max-w-[360px] text-center text-[10px] leading-relaxed" style={{ color: colors.textMuted }}>
        <div className="mb-1.5 flex items-center justify-center gap-2">
          <a href={isKo ? '/terms_ko.html' : '/terms_en.html'} target="_blank" rel="noopener"
            style={{ color: colors.textSecondary, textDecoration: 'underline' }}>
            {isKo ? '서비스 이용약관' : 'Terms of Service'}
          </a>
          <span>|</span>
          <a href={isKo ? '/privacy_ko.html' : '/privacy_en.html'} target="_blank" rel="noopener"
            style={{ color: colors.textSecondary, textDecoration: 'underline' }}>
            {isKo ? '개인정보처리방침' : 'Privacy Policy'}
          </a>
        </div>
        <p>&copy; 2025-{new Date().getFullYear()} <a href="https://ourhertz.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{isKo ? '우리들의 주파수' : 'OurHertz'}</a>. All rights reserved.</p>
        <p className="mt-1">Web v{__APP_VERSION__}</p>
      </div>

      {!Capacitor.isNativePlatform() && (
        <Link to="/" className="mt-3 text-xs" style={{ color: colors.primary }}>
          ← {isKo ? '홈으로' : 'Back to Home'}
        </Link>
      )}

      {/* Click outside to close share menu */}
      {showShareMenu && <div className="fixed inset-0 z-0" onClick={() => setShowShareMenu(false)} />}

      {/* Session conflict modal */}
      {sessionConflict && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="mx-4 w-full max-w-[340px] rounded-xl p-5"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="mb-3 text-center text-2xl">&#x26A0;&#xFE0F;</div>
            <h3 className="mb-2 text-center text-sm font-bold" style={{ color: colors.text }}>
              {t('session.conflictTitle')}
            </h3>
            <p className="mb-1 text-center text-xs leading-relaxed" style={{ color: colors.textMuted }}>
              {t('session.conflictMessage')}
            </p>
            {sessionConflict.device && (
              <p className="mb-4 text-center text-xs" style={{ color: colors.textSecondary }}>
                {t('session.conflictDevice', { device: sessionConflict.device })}
              </p>
            )}
            <div className="flex gap-2">
              <button onClick={cancelSessionConflict}
                className="flex-1 rounded-lg py-2.5 text-sm font-semibold"
                style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
                {t('common.cancel')}
              </button>
              <button onClick={resolveSessionConflict} disabled={loading}
                className="flex-1 rounded-lg py-2.5 text-sm font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: colors.warning }}>
                {loading ? t('common.loading') : t('session.forceLogin')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
